import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from 'config';

interface PricelistFile {
  _id: string;
  name: string;
  fileUrl: string;
  order: number;
}

interface PricelistCategory {
  _id: string;
  name: string;
  files: PricelistFile[];
  order: number;
}

// Skeleton data for loading state
const skeletonCategories = [
  { id: 1, files: Array(3).fill(null) },
  { id: 2, files: Array(3).fill(null) },
  { id: 3, files: Array(2).fill(null) }
];

const PriceListsPage = () => {
  const [categories, setCategories] = useState<PricelistCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPricelists = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/pricelists`);
        setCategories(response.data);
      } catch (err) {
        console.error('Error fetching pricelists:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPricelists();
  }, []);

  const handleDownload = (fileUrl: string) => {
    window.open(`${API_URL}${fileUrl}`, '_blank');
  };

  const renderSkeletonState = () => (
    <div className="space-y-8">
      {skeletonCategories.map((category) => (
        <div key={category.id}>
          <div className="h-8 bg-gray-200 rounded w-64 mb-4 animate-pulse"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {category.files.map((_, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-3/4">
                    <div className="h-6 bg-gray-200 rounded w-full animate-pulse"></div>
                  </div>
                  <div className="w-6 h-6 bg-gray-200 rounded animate-pulse"></div>
                </div>
                <div className="h-10 bg-gray-200 rounded w-full animate-pulse"></div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold mb-6">Прайс-листы</h1>
      
      {isLoading || categories.length === 0 ? (
        renderSkeletonState()
      ) : (
        <div className="space-y-8">
          {categories.map((category) => (
            <div key={category._id}>
              <h2 className="text-2xl font-semibold mb-4">{category.name}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.files.map((file) => (
                  <div key={file._id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">{file.name}</h3>
                      </div>
                      <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <button 
                      onClick={() => handleDownload(file.fileUrl)}
                      className="mt-4 w-full bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded-md transition-colors flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Скачать прайс-лист
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-12 bg-gray-50 rounded-lg p-8">
        <h2 className="text-2xl font-semibold mb-4">Запросить индивидуальный прайс-лист</h2>
        <p className="text-gray-600 mb-6">
          Для получения специального прайс-листа с учетом объемов закупок и условий сотрудничества, свяжитесь с нашими менеджерами.
        </p>
        <Link 
          to="/callback"
          className="bg-primary text-white px-8 py-3 rounded-md hover:bg-primary-dark transition-colors"
        >
          Отправить запрос
        </Link>
      </div>
    </div>
  );
};

export default PriceListsPage; 